import {
    ColDef,
    ColGroupDef,
    ICellRendererParams,
    ValueGetterParams,
} from 'ag-grid-community';
import { t } from 'i18next';
import { TEXT_FILTER } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import {
    createBrutSimulatedColumn,
    createRealizedColumn,
    getAmountColumns,
    isFooterRow,
    isRowGrouped,
    isRowPinned,
} from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    PAY_COMPONENT,
    ENVELOPE_PAY_COMPONENT_HYPOTHESIS,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { getGroupedOrgChartHrColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { envelopePayComponentHypothesisValueGetter } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/PayComponents/helpers/gridHelpers';
import { SimulationPayComponentsData } from './definitions';
import { PayComponentsDataGridContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/PayComponents/types';
import { ORG_CHART_HR_SOFTWARE_ID } from 'routes/HumanResourcesModule/business/definitions';

const envelopePayComponentHypothesisCellRendererSelector = (
    params: ICellRendererParams<SimulationPayComponentsData>,
) => {
    if (isFooterRow(params)) return { component: undefined };
    if (isRowGrouped(params)) return { component: undefined };
    if (isRowPinned(params)) return { component: undefined };
    return {
        component:
            params.value !== undefined
                ? 'editEnvelopePayComponentHypothesisCellRenderer'
                : 'createEnvelopePayComponentHypothesisCellRenderer',
    };
};
const envelopePayComponentHypothesisFilterValueGetter = (
    params: ValueGetterParams<SimulationPayComponentsData>,
) => {
    const {
        data,
        context: { envelopePayComponentHypotheses },
    } = params;
    const hypotheses: PayComponentsDataGridContext['envelopePayComponentHypotheses'] =
        envelopePayComponentHypotheses;
    if (!data) return undefined;
    const hypothesis = hypotheses.find(
        (hyp) =>
            hyp[ORG_CHART_HR_SOFTWARE_ID] === data[ORG_CHART_HR_SOFTWARE_ID] &&
            hyp[PAY_COMPONENT] === data[PAY_COMPONENT],
    );
    if (hypothesis) {
        return t('humanResources.simulation.editHypothesis');
    }
    return '';
};
export const getPayComponentsColDefs = ({
    hrSimulation,
    // the props is used in the filter value getter helper
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    envelopePayComponentHypotheses,
}: {
    hrSimulation: HrSimulation;
    envelopePayComponentHypotheses: PayComponentsDataGridContext['envelopePayComponentHypotheses'];
}): (ColDef | ColGroupDef)[] => {
    const betweenCurrentAndSimulatedDateColumnGetters = [
        createRealizedColumn,
        createBrutSimulatedColumn,
    ];
    const pastDateColumnGetters = [createRealizedColumn];
    const simulatedDateColumnGetters = [createBrutSimulatedColumn];
    return [
        getGroupedOrgChartHrColumn(),
        {
            ...TEXT_FILTER,
            headerName: t('humanResources.simulation.payComponent'),
            field: PAY_COMPONENT,
            enableRowGroup: true,
            suppressColumnsToolPanel: true,
            lockPosition: 'left',
            pinned: 'left',
        },
        {
            ...TEXT_FILTER,
            headerName: t('humanResources.simulation.hypothesis'),
            field: ENVELOPE_PAY_COMPONENT_HYPOTHESIS,
            cellRendererSelector:
                envelopePayComponentHypothesisCellRendererSelector,
            valueGetter: envelopePayComponentHypothesisValueGetter,
            filterValueGetter: envelopePayComponentHypothesisFilterValueGetter,
            cellRendererParams: { hrSimulation },
            width: 250,
            lockPosition: 'left',
            pinned: 'left',
            suppressColumnsToolPanel: true,
        },
        ...getAmountColumns({
            simulationStart: hrSimulation.startDate,
            simulationEnd: hrSimulation.endDate,
            betweenCurrentAndSimulatedDateColumnGetters,
            pastDateColumnGetters,
            simulatedDateColumnGetters,
        }),
    ];
};
